import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Navigation from '../components/Navigation';
import EditableEmail from '../components/EditableEmail';
import { tasksAPI, clientsAPI } from '../services/api';
import type { Task } from '../types';
import { Button, Dialog, Heading, Modal, ModalOverlay } from 'react-aria-components';
import { ScrollText, X, Trash2, ChevronLeft, ChevronRight, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import { formatLocalDateTime } from '../utils/timezone';
import { safeHttpUrl } from '../utils/safeUrl';

type Period = '7days' | '1month' | '3months' | '6months' | '1year';
type SortField = 'start_time' | 'end_time' | 'created_at';
type SortDirection = 'asc' | 'desc';

// interface TasksResponse { // Unused interface removed
//   tasks: Task[];
//   total_count: number;
//   period: string;
//   start_date: string;
//   end_date: string;
// }

const TasksPage: React.FC = () => {
  const { user } = useAuth();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState<Period>('7days');
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sourceFilter, setSourceFilter] = useState<string>('all');
  const [sortField, setSortField] = useState<SortField>('created_at');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [sendingReminder, setSendingReminder] = useState<number | null>(null);
  const [recreatingMeet, setRecreatingMeet] = useState<number | null>(null);
  const [logsModalOpen, setLogsModalOpen] = useState(false);
  const [selectedLogs, setSelectedLogs] = useState<{
    logs: Array<{ timestamp: string; level: string; message: string; details?: any }>;
    summary: any;
    task_id: number;
    client_email: string;
    client_name: string;
  } | null>(null);
  const [rawLogs, setRawLogs] = useState<string>('');
  const [logsFormat, setLogsFormat] = useState<'json' | 'raw'>('raw');
  const [loadingLogs, setLoadingLogs] = useState(false);

  const loadTasks = useCallback(async () => {
    try {
      setLoading(true);
      const ordering = sortDirection === 'asc' ? sortField : `-${sortField}`;
      const response = await tasksAPI.getFiltered({
        period: selectedPeriod,
        page: currentPage,
        page_size: pageSize,
        search: searchTerm,
        status: statusFilter,
        source: sourceFilter,
        ordering: ordering,
      });
      setTasks(response.tasks);
      setTotalCount(response.total_count);
    } catch (err: any) {
      console.error('Failed to load tasks:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedPeriod, currentPage, pageSize, searchTerm, statusFilter, sourceFilter, sortField, sortDirection]);

  const handleEmailUpdate = useCallback(async (oldEmail: string, newEmail: string) => {
    await clientsAPI.updateEmailByEmail(oldEmail, newEmail);
    // Reload tasks to reflect the change
    loadTasks();
  }, [loadTasks]);

  // Helper function to convert JSON logs to raw text format
  const formatLogsToText = (logs: Array<{ timestamp: string; level: string; message: string; details?: any }>): string => {
    if (!logs || logs.length === 0) {
      return 'No logs available';
    }

    return logs.map(log => {
      const timestamp = new Date(log.timestamp).toLocaleString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
        fractionalSecondDigits: 3
      }).replace(',', '');
      
      const level = (log.level || 'info').toUpperCase().padEnd(5);
      const loggerName = log.details?.logger_name || log.details?.logger || 'system';
      let line = `${level} ${timestamp} ${loggerName} ${log.message}`;
      
      // Add details if present (excluding logger_name/logger to avoid duplication)
      if (log.details) {
        const detailsToShow = { ...log.details };
        delete detailsToShow.logger_name;
        delete detailsToShow.logger;
        
        const detailKeys = Object.keys(detailsToShow);
        if (detailKeys.length > 0) {
          line += ` ${JSON.stringify(detailsToShow)}`;
        }
      }
      
      return line;
    }).join('\n');
  };

  const handleViewLogs = async (task: Task) => {
    try {
      setLoadingLogs(true);
      setLogsFormat('raw'); // Default to raw format
      
      // Load JSON format first (this works reliably)
      const jsonData = await tasksAPI.getLogs(task.id, 'json');
      
      // Handle response - check if it's wrapped in {status, data} or direct object
      let logsData = jsonData;
      if (jsonData && typeof jsonData === 'object' && 'data' in jsonData && 'status' in jsonData) {
        // Response is wrapped: {status: 200, data: {...}}
        logsData = (jsonData as any).data;
      }
      
      if (logsData && typeof logsData === 'object' && 'logs' in logsData) {
        // Use data from API response (more up-to-date than task object)
        const logsToSet = {
          logs: logsData.logs || [],
          summary: logsData.summary || { total: 0, by_level: {}, latest: null },
          task_id: logsData.task_id || task.id,
          client_email: logsData.client_email || task.client_email,
          client_name: logsData.client_name || task.client_name,
        };
        setSelectedLogs(logsToSet);
        
        // Try to load raw format, but if it fails, generate from JSON
        let rawData = '';
        try {
          const rawResponse = await tasksAPI.getLogs(task.id, 'raw');
          rawData = typeof rawResponse === 'string' ? rawResponse : String(rawResponse || '');
          if (!rawData || rawData.trim() === '') {
            // If raw format is empty, generate from JSON
            rawData = formatLogsToText(logsToSet.logs);
          }
        } catch (rawError) {
          console.warn('Failed to load raw logs format, generating from JSON:', rawError);
          // Generate raw format from JSON data
          rawData = formatLogsToText(logsToSet.logs);
        }
        setRawLogs(rawData);
      } else {
        console.warn('Unexpected logs response format:', logsData);
        // Fallback to task data if API response format is unexpected
        setSelectedLogs({
          logs: [],
          summary: { total: 0, by_level: {}, latest: null },
          task_id: task.id,
          client_email: task.client_email,
          client_name: task.client_name,
        });
        setRawLogs('No logs available');
      }
      
      setLogsModalOpen(true);
    } catch (err: any) {
      console.error('Failed to load logs:', err);
      alert('Failed to load logs: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoadingLogs(false);
    }
  };

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'confirmed':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      case 'completed':
        return 'bg-primary-100 text-primary-700';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getSourceLabel = (source: string) => {
    switch (source) {
      case 'api':
        return 'API';
      case 'external_api':
        return 'External API';
      case 'google':
        return 'Google Calendar';
      case 'internal':
        return 'Internal';
      default:
        return source;
    }
  };

  const getSourceBadgeColor = (source: string) => {
    switch (source) {
      case 'internal':
        return 'bg-green-100 text-green-800';
      case 'api':
        return 'bg-purple-100 text-purple-800';
      case 'external_api':
        return 'bg-orange-100 text-orange-800';
      case 'google':
        return 'bg-primary-100 text-primary-700';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getNotificationStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'sent':
        return 'bg-green-100 text-green-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'not_applicable':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDateTime = (dateTime: string): string => {
    // Convert UTC to user's local timezone
    return formatLocalDateTime(dateTime);
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field with default direction (desc for newest first)
      setSortField(field);
      setSortDirection('desc');
    }
    setCurrentPage(1); // Reset to first page when sorting changes
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(1); // Reset to first page when page size changes
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  const PaginationComponent = () => {
    const getPageNumbers = () => {
      const pages: (number | string)[] = [];
      const maxVisible = 7;
      
      if (totalPages <= maxVisible) {
        for (let i = 1; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        
        if (currentPage > 3) {
          pages.push('...');
        }
        
        const start = Math.max(2, currentPage - 1);
        const end = Math.min(totalPages - 1, currentPage + 1);
        
        for (let i = start; i <= end; i++) {
          pages.push(i);
        }
        
        if (currentPage < totalPages - 2) {
          pages.push('...');
        }
        
        pages.push(totalPages);
      }
      
      return pages;
    };

    return (
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-6 py-3 bg-gray-50 border-t border-gray-200">
        <div className="flex items-center gap-4">
          <div className="text-sm text-gray-700">
            {totalCount > 0 ? (
              <>Showing {(currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} tasks</>
            ) : (
              <>No tasks found</>
            )}
          </div>
          {totalCount > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Show:</span>
              <select
                value={pageSize}
                onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                className="px-3 py-1 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-accent-500 focus:border-accent-500 outline-none bg-white"
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span className="text-sm text-gray-600">per page</span>
            </div>
          )}
        </div>
        {totalPages > 1 && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="p-2 text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed rounded-md hover:bg-gray-100 transition-colors"
              aria-label="Previous page"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            
            <div className="flex items-center gap-1">
              {getPageNumbers().map((page, index) => {
                if (page === '...') {
                  return (
                    <span key={`ellipsis-${index}`} className="px-2 text-gray-500">
                      ...
                    </span>
                  );
                }
                
                const pageNum = page as number;
                const isActive = pageNum === currentPage;
                
                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`px-3 py-1 text-sm rounded-md transition-colors ${
                      isActive
                        ? 'bg-accent-500 text-white font-semibold'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            
            <button
              onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="p-2 text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed rounded-md hover:bg-gray-100 transition-colors"
              aria-label="Next page"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>
    );
  };

  const handleSendReminder = async (taskId: number) => {
    try {
      setSendingReminder(taskId);
      await tasksAPI.sendReminder(taskId);
      // Reload tasks to get updated status
      await loadTasks();
    } catch (err: any) {
      console.error('Failed to send reminder:', err);
      alert('Failed to send reminder: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSendingReminder(null);
    }
  };

  const handleRecreateGoogleMeet = async (taskId: number) => {
    try {
      setRecreatingMeet(taskId);
      await tasksAPI.recreateGoogleMeet(taskId);
      // Reload tasks to get updated Google Meet link
      await loadTasks();
      alert('Google Meet link created successfully!');
    } catch (err: any) {
      console.error('Failed to recreate Google Meet:', err);
      alert('Failed to recreate Google Meet: ' + (err.response?.data?.error || err.response?.data?.detail || err.message));
    } finally {
      setRecreatingMeet(null);
    }
  };

  const handleDelete = async (taskId: number) => {
    if (!window.confirm('Are you sure you want to delete this task? This action cannot be undone.')) {
      return;
    }
    
    try {
      await tasksAPI.delete(taskId);
      await loadTasks();
      alert('Task deleted successfully');
    } catch (err: any) {
      console.error('Failed to delete task:', err);
      alert('Failed to delete: ' + (err.response?.data?.detail || err.message));
    }
  };

  // Check if user has access
  if (user && !user.can_view_all_tasks) {
    return (
      <>
        <Navigation />
        <div className="min-h-screen bg-gray-50">
          <div className="max-w-full mx-auto px-2 sm:px-4 lg:px-6 py-8">
            <div className="bg-red-50 border-2 border-red-200 rounded-xl p-8 text-center">
              <h1 className="text-2xl font-bold text-red-900 mb-4">⛔ Access Denied</h1>
              <p className="text-red-800">Only Owners and Administrators can view task details.</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (loading) {
    return (
      <>
        <Navigation />
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-accent-500"></div>
            <p className="mt-4 text-gray-600">Loading tasks...</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navigation />
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-full mx-auto px-2 sm:px-4 lg:px-6 py-8">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-gray-900">📋 Tasks Extended View</h1>
            <p className="mt-2 text-gray-600">
              Detailed view of all tasks with filtering and search capabilities
            </p>
          </div>

          {/* Filters and Controls */}
          <div className="mb-6 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Period Selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Time Period:
                </label>
                <select
                  value={selectedPeriod}
                  onChange={(e) => setSelectedPeriod(e.target.value as Period)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-500"
                >
                  <option value="7days">Last 7 Days</option>
                  <option value="1month">Last Month</option>
                  <option value="3months">Last 3 Months</option>
                  <option value="6months">Last 6 Months</option>
                  <option value="1year">Last Year</option>
                </select>
              </div>

              {/* Search */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Search:
                </label>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Client name, email..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-500"
                />
              </div>

              {/* Status Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status:
                </label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-500"
                >
                  <option value="all">All Statuses</option>
                  <option value="pending">Pending</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="completed">Completed</option>
                </select>
              </div>

              {/* Source Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Source:
                </label>
                <select
                  value={sourceFilter}
                  onChange={(e) => setSourceFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-500"
                >
                  <option value="all">All Sources</option>
                  <option value="internal">Internal (UI)</option>
                  <option value="api">API</option>
                  <option value="external_api">External API</option>
                  <option value="google">Google Calendar</option>
                </select>
              </div>
            </div>

            {/* Refresh Button */}
            <div className="mt-4 flex justify-end">
              <button
                onClick={loadTasks}
                className="px-4 py-2 bg-accent-500 text-white rounded-lg hover:bg-accent-600 transition text-sm font-medium"
              >
                🔄 Refresh
              </button>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <div className="flex items-center">
                <div className="bg-primary-50 rounded-full p-2 mr-3">
                  <svg className="h-6 w-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Total Tasks</p>
                  <p className="text-2xl font-bold text-gray-900">{totalCount}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <div className="flex items-center">
                <div className="bg-green-100 rounded-full p-2 mr-3">
                  <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Confirmed</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {tasks.filter(t => t.status === 'confirmed').length}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <div className="flex items-center">
                <div className="bg-purple-100 rounded-full p-2 mr-3">
                  <svg className="h-6 w-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm text-gray-600">API Tasks</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {tasks.filter(t => t.source === 'api').length}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <div className="flex items-center">
                <div className="bg-orange-100 rounded-full p-2 mr-3">
                  <svg className="h-6 w-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-9-9c2.52 0 4.93 1 6.74 2.74L21 8" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm text-gray-600">External API Tasks</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {tasks.filter(t => t.source === 'external_api').length}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <div className="flex items-center">
                <div className="bg-primary-50 rounded-full p-2 mr-3">
                  <svg className="h-6 w-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Google Tasks</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {tasks.filter(t => t.source === 'google').length}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <div className="flex items-center">
                <div className="bg-green-100 rounded-full p-2 mr-3">
                  <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Reminders Sent</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {tasks.filter(t => t.reminder_notification_status === 'sent').length}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Tasks Table */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="px-3 py-3 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">Tasks Details</h2>
            </div>

            {/* Pagination - Top */}
            {totalCount > 0 && <PaginationComponent />}

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Client
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Manager
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      <button
                        onClick={() => handleSort('start_time')}
                        className="flex items-center gap-1 hover:text-gray-900 transition-colors"
                      >
                        Start Time
                        {sortField === 'start_time' ? (
                          sortDirection === 'asc' ? (
                            <ArrowUp className="w-4 h-4" />
                          ) : (
                            <ArrowDown className="w-4 h-4" />
                          )
                        ) : (
                          <ArrowUpDown className="w-4 h-4 text-gray-400" />
                        )}
                      </button>
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      <button
                        onClick={() => handleSort('end_time')}
                        className="flex items-center gap-1 hover:text-gray-900 transition-colors"
                      >
                        End Time
                        {sortField === 'end_time' ? (
                          sortDirection === 'asc' ? (
                            <ArrowUp className="w-4 h-4" />
                          ) : (
                            <ArrowDown className="w-4 h-4" />
                          )
                        ) : (
                          <ArrowUpDown className="w-4 h-4 text-gray-400" />
                        )}
                      </button>
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Source
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Google Meet
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Reminder Status
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      <button
                        onClick={() => handleSort('created_at')}
                        className="flex items-center gap-1 hover:text-gray-900 transition-colors"
                      >
                        Created
                        {sortField === 'created_at' ? (
                          sortDirection === 'asc' ? (
                            <ArrowUp className="w-4 h-4" />
                          ) : (
                            <ArrowDown className="w-4 h-4" />
                          )
                        ) : (
                          <ArrowUpDown className="w-4 h-4 text-gray-400" />
                        )}
                      </button>
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {tasks.length === 0 ? (
                    <tr>
                      <td colSpan={11} className="px-3 py-8 text-center text-gray-500">
                        No tasks found
                      </td>
                    </tr>
                  ) : (
                    tasks.map((task) => (
                      <tr key={task.id} className="hover:bg-gray-50 transition">
                        <td className="px-3 py-2">
                          <div>
                            <div className="text-xs font-semibold text-gray-900">
                              {task.client_name}
                            </div>
                            <div className="text-xs text-gray-500">
                              {task.client_email ? (
                                <EditableEmail
                                  email={task.client_email}
                                  onUpdate={(newEmail) => handleEmailUpdate(task.client_email, newEmail)}
                                />
                              ) : (
                                <span className="text-gray-400">No email</span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-6 w-6 bg-accent-500 rounded-full flex items-center justify-center text-white font-semibold text-xs">
                              {task.manager_details?.first_name?.[0] || 'M'}
                            </div>
                            <div className="ml-2">
                              <div className="text-xs font-semibold text-gray-900">
                                {task.manager_details?.first_name} {task.manager_details?.last_name}
                              </div>
                              <div className="text-xs text-gray-500">
                                {task.manager_details?.email}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-900">
                          {formatDateTime(task.start_time)}
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-900">
                          {formatDateTime(task.end_time)}
                        </td>
                        <td className="px-3 py-2">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeColor(task.status)}`}>
                            {task.status}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getSourceBadgeColor(task.source)}`}>
                            {getSourceLabel(task.source)}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-900 max-w-48">
                          <div className="truncate" title={task.notes}>
                            {task.notes || '-'}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-xs">
                          {safeHttpUrl(task.google_meet_link) ? (
                            <a
                              href={safeHttpUrl(task.google_meet_link)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-accent-600 hover:text-accent-700 underline"
                            >
                              Join Meeting
                            </a>
                          ) : (
                            <button
                              onClick={() => handleRecreateGoogleMeet(task.id)}
                              disabled={recreatingMeet === task.id}
                              className="px-2 py-1 text-xs bg-accent-500 text-white rounded hover:bg-accent-600 disabled:bg-gray-400 disabled:cursor-not-allowed"
                              title="Recreate Google Meet link"
                            >
                              {recreatingMeet === task.id ? 'Creating...' : 'Recreate Meet'}
                            </button>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex flex-col">
                            <span className={`inline-flex justify-center items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getNotificationStatusBadgeColor(task.reminder_notification_status)}`}>
                              {task.reminder_notification_status}
                            </span>
                            {task.reminder_notification_sent_at && (
                              <span className="text-xs text-gray-500 mt-1">
                                {formatDateTime(task.reminder_notification_sent_at)}
                              </span>
                            )}
                            {task.reminder_notification_message_id && (
                              <span className="text-xs text-accent-500 mt-1" title={`Postmark MessageID: ${task.reminder_notification_message_id}`}>
                                ID: {task.reminder_notification_message_id.substring(0, 8)}...
                              </span>
                            )}
                            {task.reminder_notification_error && (
                              <span className="text-xs text-red-500 mt-1" title={task.reminder_notification_error}>
                                Error: {task.reminder_notification_error.substring(0, 30)}...
                              </span>
                            )}
                            {(task.reminder_notification_status === 'pending' || task.reminder_notification_status === 'failed') && (
                              <button
                                onClick={() => handleSendReminder(task.id)}
                                disabled={sendingReminder === task.id}
                                className="mt-1 px-2 py-1 text-xs bg-accent-500 text-white rounded hover:bg-accent-600 disabled:opacity-50 disabled:cursor-not-allowed transition"
                              >
                                {sendingReminder === task.id ? 'Sending...' : 'Send Now'}
                              </button>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-500">
                          {formatDateTime(task.created_at)}
                        </td>
                        <td className="px-3 py-2 text-sm font-medium">
                          <div className="flex items-center gap-2">
                          <Button
                            onPress={() => handleViewLogs(task)}
                            isDisabled={loadingLogs}
                            className="flex items-center gap-1 px-3 py-1.5 text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-xs"
                          >
                            <ScrollText className="w-4 h-4" />
                            View Log
                          </Button>
                            {user?.role === 'owner' && (
                              <Button
                                onPress={() => handleDelete(task.id)}
                                className="flex items-center gap-1 px-3 py-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors text-xs"
                              >
                                <Trash2 className="w-4 h-4" />
                                Delete
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination - Bottom */}
            {totalCount > 0 && <PaginationComponent />}
          </div>
        </div>
      </div>

      {/* Logs Modal */}
      <ModalOverlay 
        isOpen={logsModalOpen} 
        onOpenChange={setLogsModalOpen}
        className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      >
        <Modal className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden">
          <Dialog className="flex flex-col h-full min-h-0 outline-none">
            {({ close }) => (
              <>
                <div className="flex-shrink-0 flex items-center justify-between p-6 border-b border-gray-200">
                  <Heading className="text-xl font-semibold text-gray-900">
                    Task #{selectedLogs?.task_id} - Processing Logs
                    {selectedLogs && (
                      <span className="text-sm font-normal text-gray-500 ml-2">
                        ({selectedLogs.client_name} - {selectedLogs.client_email})
                      </span>
                    )}
                  </Heading>
                  <Button
                    onPress={close}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <X className="w-6 h-6" />
                  </Button>
                </div>

                <div className="flex-1 min-h-0 overflow-y-auto p-6">
                  {selectedLogs && (
                    <>
                      {/* Summary */}
                      <div className="mb-6 bg-gray-50 rounded-lg p-4">
                        <h3 className="text-sm font-semibold text-gray-700 mb-2">Summary</h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div>
                            <div className="text-2xl font-bold text-primary-600">{selectedLogs.summary.total}</div>
                            <div className="text-xs text-gray-600">Total Logs</div>
                          </div>
                          {Object.entries(selectedLogs.summary.by_level).map(([level, count]) => (
                            <div key={level}>
                              <div className="text-2xl font-bold text-primary-600">{count as number}</div>
                              <div className="text-xs text-gray-600 capitalize">{level}</div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Format Toggle */}
                      <div className="mb-4 flex items-center justify-between">
                        <h3 className="text-sm font-semibold text-gray-700">Log Entries</h3>
                        <div className="flex gap-2">
                          <button
                            onClick={() => setLogsFormat('raw')}
                            className={`px-3 py-1 text-xs rounded-md transition-colors ${
                              logsFormat === 'raw'
                                ? 'bg-primary-600 text-white'
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            }`}
                          >
                            Raw
                          </button>
                          <button
                            onClick={() => setLogsFormat('json')}
                            className={`px-3 py-1 text-xs rounded-md transition-colors ${
                              logsFormat === 'json'
                                ? 'bg-primary-600 text-white'
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            }`}
                          >
                            Structured
                          </button>
                        </div>
                      </div>

                      {/* Logs Display */}
                      {logsFormat === 'raw' ? (
                        <div className="bg-gray-900 text-green-400 p-4 rounded-lg font-mono text-xs max-h-96 overflow-y-auto">
                          <pre className="whitespace-pre-wrap break-words">{rawLogs || 'No logs available'}</pre>
                        </div>
                      ) : (
                      <div className="space-y-2">
                          {selectedLogs && selectedLogs.logs.length === 0 ? (
                          <div className="text-center py-8 text-gray-500">
                            No logs available for this task
                          </div>
                        ) : (
                          <div className="space-y-2 max-h-96 overflow-y-auto">
                              {selectedLogs?.logs.map((log, index) => (
                              <div
                                key={index}
                                className={`border-l-4 p-3 rounded-r bg-gray-50 ${
                                  log.level === 'error' ? 'border-red-500' :
                                  log.level === 'warning' ? 'border-yellow-500' :
                                  log.level === 'success' ? 'border-green-500' :
                                  'border-blue-500'
                                }`}
                              >
                                <div className="flex items-start justify-between mb-1">
                                  <span className={`text-xs font-semibold capitalize ${
                                    log.level === 'error' ? 'text-red-700' :
                                    log.level === 'warning' ? 'text-yellow-700' :
                                    log.level === 'success' ? 'text-green-700' :
                                    'text-blue-700'
                                  }`}>
                                    {log.level}
                                  </span>
                                  <span className="text-xs text-gray-500">
                                    {new Date(log.timestamp).toLocaleString()}
                                  </span>
                                </div>
                                <div className="text-sm text-gray-800">{log.message}</div>
                                {log.details && (
                                  <details className="mt-2">
                                    <summary className="text-xs text-gray-600 cursor-pointer hover:text-gray-800">
                                      Details
                                    </summary>
                                    <pre className="mt-2 text-xs bg-gray-100 p-2 rounded overflow-x-auto">
                                      {JSON.stringify(log.details, null, 2)}
                                    </pre>
                                  </details>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      )}
                    </>
                  )}
                </div>

                <div className="flex-shrink-0 flex justify-end gap-3 p-6 border-t border-gray-200 bg-white">
                  <Button
                    onPress={close}
                    className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
                  >
                    Close
                  </Button>
                </div>
              </>
            )}
          </Dialog>
        </Modal>
      </ModalOverlay>
    </>
  );
};

export default TasksPage;
